import { useEffect, useState } from 'react';
import { useCreateDepositOperation, useOperationDetails } from '@/shared/api';
import { useDepositData, useDepositFormLogic } from '@/shared/lib';
import {
  type DepositFormValues,
  type OperationDetailsResponse,
} from '@/shared/types';
import { type CardDepositResponse } from '../api';
import { usePaymentWidget } from './';

export type DepositCardFormValues = DepositFormValues;

const SUCCESS_STATUSES = ['success', 'pending'];

interface UseDepositCardFormLogicOptions {
  onSubmit?: (data: DepositCardFormValues) => void;
  onSuccess?: (data: { currencyCode: string; amount: number }) => void;
  onError?: (error: Error) => void;
  initialData?: DepositCardFormValues | null;
}

export const useDepositCardFormLogic = ({
  onSubmit,
  onSuccess,
  onError,
  initialData,
}: UseDepositCardFormLogicOptions = {}) => {
  const [showResultModal, setShowResultModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<DepositCardFormValues | null>(
    initialData || null
  );
  const [currentOperationId, setCurrentOperationId] = useState<string | null>(
    null
  );
  const [checkStatusOnClose, setCheckStatusOnClose] = useState(false);

  const {
    accountNumber,
    accountsInfoError,
    isAccountsInfoLoading,
    isAccountInfoError,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsIsError,
    depositWithdrawAssetsError,
    isBanksLoading,
    isBanksError,
    banksError,
  } = useDepositData();
  const sharedFormLogic = useDepositFormLogic<CardDepositResponse>({
    operationType: 'card',
    initialData,
    onSubmit: data => {
      setFormData(data);
      onSubmit?.(data);
    },
    onCreateDepositError: error => {
      setShowResultModal(true);
      setIsSuccess(false);
      onError?.(error);
    },
  });

  const {
    mutate: createDeposit,
    isCreateOperationDepositError,
    createOperationDepositError,
  } = useCreateDepositOperation<CardDepositResponse>({
    operationType: 'card',
    onSuccess: data => {
      openPaymentWidget(data);
    },
    onError: error => {
      setShowResultModal(true);
      setIsSuccess(false);
      onError?.(error);
    },
  });

  const { data: operationDetails, isLoading: isCheckingStatus } =
    useOperationDetails<OperationDetailsResponse>({
      operationId: currentOperationId || '',
      enabled: !!currentOperationId && (showResultModal || checkStatusOnClose),
      shouldPollUntil: data => data.status.paramValue === 'pending',
    });

  const { openPaymentWidget, isPaymentWidgetLoading, paymentWidgetError } =
    usePaymentWidget({
      onOpen: idPayments => {
        setCurrentOperationId(idPayments);
      },
      onClose: idPayments => {
        setCurrentOperationId(idPayments);
        setCheckStatusOnClose(true);
      },
      onError: error => {
        setShowResultModal(true);
        setIsSuccess(false);
        onError?.(error);
      },
    });

  useEffect(() => {
    if (checkStatusOnClose && operationDetails && currentOperationId) {
      setCheckStatusOnClose(false);

      const isOperationSuccess = SUCCESS_STATUSES.includes(
        operationDetails.status.paramValue.toLowerCase()
      );

      setShowResultModal(true);
      setIsSuccess(isOperationSuccess);

      if (isOperationSuccess) {
        onSuccess?.({
          currencyCode: formData?.currencyCode || '',
          amount: Number(formData?.amount),
        });
      }
    }
  }, [
    checkStatusOnClose,
    operationDetails,
    currentOperationId,
    formData,
    onSuccess,
  ]);

  const handleFormSubmit = (data: DepositCardFormValues) => {
    if (sharedFormLogic.isSubmitInProgressWithoutErrors) {
      return;
    }

    setFormData(data);
    setIsSuccess(false);
    setShowResultModal(false);
    createDeposit({
      currencyCode: data.currencyCode,
      amount: data.amount,
      accountNumber,
    });

    onSubmit?.(data);
  };

  const resultModalClick = () => {
    if (isSuccess) {
      onSuccess?.({
        currencyCode: formData?.currencyCode || '',
        amount: Number(formData?.amount),
      });
      return;
    }

    setShowResultModal(false);
  };

  // Error states
  const hasDataError =
    depositWithdrawAssetsIsError ||
    isBanksError ||
    isCreateOperationDepositError;

  let firstError = null;

  if (isCreateOperationDepositError) {
    firstError = createOperationDepositError;
  } else if (depositWithdrawAssetsIsError) {
    firstError = depositWithdrawAssetsError;
  } else if (isBanksError) {
    firstError = banksError;
  } else if (paymentWidgetError) {
    firstError = paymentWidgetError;
  }

  const isLoading =
    depositWithdrawAssetsLoading || isBanksLoading || isAccountsInfoLoading;

  return {
    // Form
    form: sharedFormLogic.form,
    watchedValues: sharedFormLogic.watchedValues,
    handleFormSubmit,

    // Data (from shared logic)
    currencyOptions: sharedFormLogic.currencyOptions,
    minAmount: sharedFormLogic.minAmount,
    amountPlaceholder: sharedFormLogic.amountPlaceholder,

    // Commission (from shared logic)
    commissionData: sharedFormLogic.commissionData,
    isCommissionFetching: sharedFormLogic.isCommissionFetching,
    commissionError: sharedFormLogic.commissionError,
    shouldShowCommission: sharedFormLogic.shouldShowCommission,
    isCommissionPending: sharedFormLogic.isCommissionPending,

    // Card-specific states
    showResultModal,
    isSuccess,
    isCheckingStatus,
    operationDetails,
    isSubmitInProgressWithoutErrors:
      sharedFormLogic.isSubmitInProgressWithoutErrors || isPaymentWidgetLoading,
    formError: sharedFormLogic.formError,

    // Loading states
    isLoading,
    isAccountInfoError,
    accountsInfoError,

    // Error states
    hasDataError,
    firstError,

    // Handlers
    resultModalClick,
  };
};
